// 方案文档预览/批注 —— 与 /api/documents/* 交互的客户端封装。

import type { Chapter } from "./data";
import { chapterHeading, documentOrder, matchChapterHeading } from "./chapterLayout";

export interface DocRecord {
  id: string;
  conversation_id: string;
  user_id: number;
  title: string;
  content: string;
  created_at: number;
  updated_at: number;
}

export interface Annotation {
  id: number;
  document_id: string;
  user_id: number;
  quote: string;
  /** 引用文本在文档中的第几次出现（0-based），用于消歧同一段文字的重复出现。 */
  quote_index: number;
  /** 创建批注时所在的章节号；完整文档视图下创建的（或旧数据）为 null。 */
  chapter_num: string | null;
  comment: string;
  resolved: number;
  created_at: number;
}

async function parseJson(res: Response): Promise<any> {
  return res.json().catch(() => ({}));
}

export async function getDocument(conversationId: string): Promise<{ document: DocRecord; annotations: Annotation[] }> {
  const res = await fetch(`/api/documents/${conversationId}`, { credentials: "same-origin" });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "加载文档失败");
  return data;
}

export async function saveDocumentContent(conversationId: string, content: string): Promise<DocRecord> {
  const res = await fetch(`/api/documents/${conversationId}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "保存失败");
  return data.document;
}

export async function addAnnotation(
  conversationId: string,
  quote: string,
  comment: string,
  quoteIndex = 0,
  chapterNum: string | null = null
): Promise<Annotation> {
  const res = await fetch(`/api/documents/${conversationId}/annotations`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ quote, comment, quoteIndex, chapterNum }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "添加批注失败");
  return data.annotation;
}

export async function setAnnotationResolved(conversationId: string, annotationId: number, resolved: boolean): Promise<void> {
  const res = await fetch(`/api/documents/${conversationId}/annotations/${annotationId}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ resolved }),
  });
  if (!res.ok) throw new Error("更新批注状态失败");
}

export async function deleteAnnotation(conversationId: string, annotationId: number): Promise<void> {
  const res = await fetch(`/api/documents/${conversationId}/annotations/${annotationId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("删除批注失败");
}

export interface DocSection {
  num: string;
  title: string;
  body: string;
}

/** 把整份合并文档按 `## {章节号} {标题}` 拆回各章节小节。
 *  逻辑与服务端 splitMergedDocument 保持一致，用于单章节预览/编辑。 */
export function splitDocumentSections(content: string, chapters: Chapter[]): DocSection[] {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const hits: { index: number; chapter: Chapter }[] = [];
  lines.forEach((line, i) => {
    const chapter = matchChapterHeading(line, chapters);
    if (chapter && !hits.some((h) => h.chapter.num === chapter.num)) hits.push({ index: i, chapter });
  });
  return hits.map((hit, i) => {
    const end = i + 1 < hits.length ? hits[i + 1]!.index : lines.length;
    const body = lines
      .slice(hit.index + 1, end)
      .join("\n")
      .replace(/\n*^-{3,}\s*$/m, "")
      .trim();
    return { num: hit.chapter.num, title: hit.chapter.title, body };
  });
}

/** 把（可能改动过某一章的）小节列表按 chapters 顺序重新拼回整份文档，与服务端 rebuildDocumentContent 格式一致。 */
export function mergeDocumentSections(sections: DocSection[], chapters: Chapter[]): string {
  const byNum = new Map(sections.map((s) => [s.num, s]));
  const parts: string[] = [];
  for (const chapter of documentOrder(chapters)) {
    const s = byNum.get(chapter.num);
    if (!s || !s.body.trim()) continue;
    parts.push(`## ${chapterHeading(chapter)}\n\n${s.body.trim()}`);
  }
  return parts.join("\n\n---\n\n");
}
